/**
 * EditCommunicationDialog — Communication & invoicing prefs
 * (migration 0031). Mirrors the rich `CommunicationSection` block
 * from the dead customer form:
 *
 *   1. Default delivery channel — 4-tile picker (WhatsApp / Email /
 *      SMS / Print). WhatsApp is the V1 default; reminders fall
 *      back to email if WhatsApp delivery fails. SMS is OTP-only
 *      per system-decisions §6.
 *   2. Reminder cadence — 4 toggle tiles (7d before / 1d before /
 *      on due / 7d after). Auto-pauses once paid.
 *   3. Monthly statement on 1st (toggle).
 *   4. Marketing & promotional sends (toggle).
 *
 * @see prototype/sales/customer-new.html §4 — visual baseline
 * @see packages/db/migrations/0031_customer_communication_prefs.sql
 */
import * as React from "react";
import { FileText, Mail, MessageCircle, Phone } from "lucide-react";
import { cn } from "@flint/ui";
import type { customers as contract } from "@flint/api-contract";
import type { z } from "zod";
import { EditDialog } from "./edit-dialog.tsx";
import { SectionField } from "./section-field.tsx";

type CustomerRow = z.infer<typeof contract.customerRow>;
type Channel = "whatsapp" | "email" | "sms" | "print";

const CHANNELS: ReadonlyArray<{
  value: Channel;
  label: string;
  meta: string;
  icon: React.ReactNode;
}> = [
  {
    value: "whatsapp",
    label: "WhatsApp",
    meta: "1 credit each",
    icon: <MessageCircle size={16} strokeWidth={2} />,
  },
  { value: "email", label: "Email", meta: "Free", icon: <Mail size={16} strokeWidth={2} /> },
  { value: "sms", label: "SMS", meta: "OTP only", icon: <Phone size={16} strokeWidth={2} /> },
  {
    value: "print",
    label: "Print only",
    meta: "Manual",
    icon: <FileText size={16} strokeWidth={2} />,
  },
];

const CADENCE_TILES: ReadonlyArray<{
  key: "remind7dBefore" | "remind1dBefore" | "remindOnDue" | "remind7dAfter";
  value: string;
  meta: string;
}> = [
  { key: "remind7dBefore", value: "7 days before", meta: "heads-up" },
  { key: "remind1dBefore", value: "1 day before", meta: "firm" },
  { key: "remindOnDue", value: "On due date", meta: "now" },
  { key: "remind7dAfter", value: "7 days after", meta: "overdue" },
];

export interface EditCommunicationDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  customer: CustomerRow;
  pending: boolean;
  serverError: string | null;
  onSave: (patch: {
    defaultChannel?: Channel;
    remind7dBefore?: boolean;
    remind1dBefore?: boolean;
    remindOnDue?: boolean;
    remind7dAfter?: boolean;
    monthlyStatement?: boolean;
    marketingOptIn?: boolean;
  }) => Promise<void>;
}

export function EditCommunicationDialog({
  open,
  onOpenChange,
  customer,
  pending,
  serverError,
  onSave,
}: EditCommunicationDialogProps) {
  const [channel, setChannel] = React.useState<Channel>(customer.defaultChannel as Channel);
  const [cadence, setCadence] = React.useState({
    remind7dBefore: customer.remind7dBefore,
    remind1dBefore: customer.remind1dBefore,
    remindOnDue: customer.remindOnDue,
    remind7dAfter: customer.remind7dAfter,
  });
  const [monthlyStatement, setMonthlyStatement] = React.useState(customer.monthlyStatement);
  const [marketingOptIn, setMarketingOptIn] = React.useState(customer.marketingOptIn);

  React.useEffect(() => {
    if (open) {
      setChannel(customer.defaultChannel as Channel);
      setCadence({
        remind7dBefore: customer.remind7dBefore,
        remind1dBefore: customer.remind1dBefore,
        remindOnDue: customer.remindOnDue,
        remind7dAfter: customer.remind7dAfter,
      });
      setMonthlyStatement(customer.monthlyStatement);
      setMarketingOptIn(customer.marketingOptIn);
    }
  }, [open, customer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const patch: Parameters<EditCommunicationDialogProps["onSave"]>[0] = {};

    if (channel !== customer.defaultChannel) patch.defaultChannel = channel;
    if (cadence.remind7dBefore !== customer.remind7dBefore)
      patch.remind7dBefore = cadence.remind7dBefore;
    if (cadence.remind1dBefore !== customer.remind1dBefore)
      patch.remind1dBefore = cadence.remind1dBefore;
    if (cadence.remindOnDue !== customer.remindOnDue) patch.remindOnDue = cadence.remindOnDue;
    if (cadence.remind7dAfter !== customer.remind7dAfter)
      patch.remind7dAfter = cadence.remind7dAfter;
    if (monthlyStatement !== customer.monthlyStatement) patch.monthlyStatement = monthlyStatement;
    if (marketingOptIn !== customer.marketingOptIn) patch.marketingOptIn = marketingOptIn;

    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }
    await onSave(patch);
  };

  return (
    <EditDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Communication & invoicing"
      description="How invoices and reminders reach this customer. Guardrails enforced per system-decisions §4."
      pending={pending}
      serverError={serverError}
      onSubmit={handleSubmit}
      maxWidthClass="max-w-xl"
    >
      <SectionField
        label="Default delivery channel"
        hint="Reminders fall back to email if WhatsApp delivery fails. SMS is OTP-only (system-decisions §6)."
      >
        <div className="grid grid-cols-2 gap-2">
          {CHANNELS.map((c) => (
            <ChannelTile
              key={c.value}
              active={channel === c.value}
              onClick={() => setChannel(c.value)}
              icon={c.icon}
              label={c.label}
              meta={c.meta}
            />
          ))}
        </div>
      </SectionField>

      <SectionField label="Reminder cadence" hint="Auto-pauses once the invoice is paid.">
        <div className="grid grid-cols-2 gap-2">
          {CADENCE_TILES.map((t) => (
            <CadenceTile
              key={t.key}
              active={cadence[t.key]}
              onClick={() => setCadence((prev) => ({ ...prev, [t.key]: !prev[t.key] }))}
              value={t.value}
              meta={t.meta}
            />
          ))}
        </div>
      </SectionField>

      <ToggleRow
        title="Monthly statement on 1st"
        body="A consolidated outstanding summary, sent on the 1st of every month — useful for buyers with multiple open invoices."
        checked={monthlyStatement}
        onChange={setMonthlyStatement}
        label="Monthly statement toggle"
      />

      <ToggleRow
        title="Marketing & promotional sends"
        body="Newsletters, festival greetings, new-product announcements. Off by default — most B2B buyers prefer transactional only."
        checked={marketingOptIn}
        onChange={setMarketingOptIn}
        label="Marketing sends toggle"
      />
    </EditDialog>
  );
}

/** Channel picker tile — icon + label + meta strip. */
function ChannelTile({
  active,
  onClick,
  icon,
  label,
  meta,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  meta: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex items-center gap-2.5 px-3 py-2 rounded-md border text-left transition-colors",
        active
          ? "border-sky-600 bg-sky-50 text-ink"
          : "border-line bg-paper text-ink-2 hover:bg-bg",
      )}
    >
      <span
        className={cn(
          "inline-grid place-items-center w-8 h-8 rounded-sm flex-shrink-0",
          active ? "bg-sky-100 text-sky-700" : "bg-bg text-muted",
        )}
        aria-hidden
      >
        {icon}
      </span>
      <span className="flex flex-col gap-0 min-w-0">
        <span className="text-sm font-semibold leading-tight">{label}</span>
        <span className="text-xs text-muted leading-tight">{meta}</span>
      </span>
    </button>
  );
}

/** Cadence toggle tile — three-line stack ("Send" / value / meta). */
function CadenceTile({
  active,
  onClick,
  value,
  meta,
}: {
  active: boolean;
  onClick: () => void;
  value: string;
  meta: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex flex-col items-start gap-0.5 px-3 py-2 rounded-md border text-left transition-colors",
        active
          ? "border-sky-600 bg-sky-50 text-ink"
          : "border-line bg-paper text-ink-2 hover:bg-bg",
      )}
    >
      <span
        className={cn(
          "text-xs uppercase tracking-wider font-semibold",
          active ? "text-sky-700" : "text-muted",
        )}
      >
        {active ? "Send" : "Skip"}
      </span>
      <span className="text-sm font-semibold">{value}</span>
      <span className="text-xs text-muted">{meta}</span>
    </button>
  );
}

/** Reuse of the same toggle pattern from tax-dialog. */
function ToggleRow({
  title,
  body,
  checked,
  onChange,
  label,
}: {
  title: string;
  body: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <label
      className="flex items-start gap-3 cursor-pointer rounded-md border border-line bg-paper p-3 hover:bg-bg/40 transition-colors"
      aria-label={label}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-ink leading-snug">{title}</div>
        <div className="text-xs text-muted leading-snug mt-0.5">{body}</div>
      </div>
      <span className="flex-shrink-0 pt-0.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4"
        />
      </span>
    </label>
  );
}
